import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ConsoleHelpArticle } from "@victor/api-client/console";
import { api } from "../../lib/api";
import { PageSpinner } from "../../ui/Spinner";
import { Button } from "../../ui/Button";
import { Modal } from "../../ui/Modal";
import { Input } from "../../ui/Input";
import { Textarea } from "../../ui/Textarea";
import { Select } from "../../ui/Select";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { Panel, EmptyInline } from "../tenants/tabs/_ui";

const TONES = [
  "accent",
  "indigo",
  "emerald",
  "amber",
  "rose",
  "violet",
  "sky",
  "slate",
].map((t) => ({ value: t, label: t }));

const ICON_HINT =
  "Noms valides : rocket, calendar, users, briefcase, shopping-bag, smartphone, shield, settings, book, mail…";

const TONE_SQUARE: Record<string, string> = {
  accent: "bg-accent-100 text-accent-700",
  indigo: "bg-indigo-100 text-indigo-700",
  emerald: "bg-emerald-100 text-emerald-700",
  amber: "bg-amber-100 text-amber-700",
  rose: "bg-rose-100 text-rose-700",
  violet: "bg-violet-100 text-violet-700",
  sky: "bg-sky-100 text-sky-700",
  slate: "bg-navy-100 text-navy-600",
};

const ICON = {
  pencil: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
  ),
  trash: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
  ),
};

function IconBtn({
  label,
  onClick,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-lg text-navy-500 transition-colors hover:bg-navy-100 ${
        danger ? "hover:text-red-600" : "hover:text-navy-800"
      }`}
    >
      {children}
    </button>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl p-4 ring-1 ${
        accent ? "bg-accent-50 ring-accent-100" : "bg-white ring-navy-100"
      }`}
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-navy-400">
        {label}
      </p>
      <p
        className={`mt-1 text-2xl font-bold ${
          accent ? "text-accent-700" : "text-navy-800"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

type ArticleForm = {
  id?: string;
  categoryId: string;
  slug: string;
  question: string;
  body: string;
  keywords: string;
  published: boolean;
  ordre: number;
};

type CatForm = {
  id?: string;
  key: string;
  title: string;
  description: string;
  icon: string;
  tone: string;
  ordre: number;
};

export function HelpAdminPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin-help"],
    queryFn: () => api.help.tree(),
  });
  const misses = useQuery({
    queryKey: ["help-search-misses"],
    queryFn: () => api.help.searchMisses(),
  });

  const [article, setArticle] = useState<ArticleForm | null>(null);
  const [cat, setCat] = useState<CatForm | null>(null);
  const [del, setDel] = useState<
    | { type: "article" | "category"; id: string; label: string }
    | null
  >(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin-help"] });

  const saveArticle = useMutation({
    mutationFn: (f: ArticleForm) =>
      f.id
        ? api.help.updateArticle(f.id, {
            categoryId: f.categoryId,
            question: f.question,
            body: f.body,
            keywords: f.keywords || null,
            published: f.published,
            ordre: f.ordre,
          })
        : api.help.createArticle({
            categoryId: f.categoryId,
            slug: f.slug,
            question: f.question,
            body: f.body,
            keywords: f.keywords || null,
            published: f.published,
            ordre: f.ordre,
          }),
    onSuccess: () => {
      setArticle(null);
      invalidate();
    },
  });

  const saveCat = useMutation({
    mutationFn: (f: CatForm) =>
      f.id
        ? api.help.updateCategory(f.id, {
            title: f.title,
            description: f.description,
            icon: f.icon,
            tone: f.tone,
            ordre: f.ordre,
          })
        : api.help.createCategory({
            key: f.key,
            title: f.title,
            description: f.description,
            icon: f.icon,
            tone: f.tone,
            ordre: f.ordre,
          }),
    onSuccess: () => {
      setCat(null);
      invalidate();
    },
  });

  const togglePublish = useMutation({
    mutationFn: (a: ConsoleHelpArticle) =>
      api.help.updateArticle(a.id, { published: !a.published }),
    onSuccess: invalidate,
  });

  const doDelete = useMutation({
    mutationFn: (d: { type: "article" | "category"; id: string }) =>
      d.type === "article"
        ? api.help.deleteArticle(d.id)
        : api.help.deleteCategory(d.id),
    onSuccess: () => {
      setDel(null);
      invalidate();
    },
  });

  if (q.isLoading) return <PageSpinner />;
  const categories = q.data ?? [];
  const catOptions = categories.map((c) => ({ value: c.id, label: c.title }));
  const allArticles = categories.flatMap((c) => c.articles);
  const nbPublies = allArticles.filter((a) => a.published).length;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-navy-800 sm:text-3xl">
            Centre d'aide
          </h1>
          <p className="mt-1 text-navy-600">
            Articles servis dans l'espace agence et utilisés par l'assistant.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              setCat({
                key: "",
                title: "",
                description: "",
                icon: "book",
                tone: "accent",
                ordre: categories.length,
              })
            }
          >
            + Catégorie
          </Button>
          <Button
            onClick={() =>
              setArticle({
                categoryId: categories[0]?.id ?? "",
                slug: "",
                question: "",
                body: "",
                keywords: "",
                published: true,
                ordre: 0,
              })
            }
            disabled={categories.length === 0}
          >
            + Article
          </Button>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Catégories" value={categories.length} />
        <Stat label="Articles" value={allArticles.length} />
        <Stat label="Publiés" value={nbPublies} accent />
        <Stat label="Brouillons" value={allArticles.length - nbPublies} />
      </div>

      {/* Recherches sans réponse */}
      {misses.data && misses.data.length > 0 ? (
        <Panel padded={false}>
          <div className="border-b border-navy-100 px-5 py-3.5">
            <h2 className="font-bold text-navy-800">Recherches sans réponse</h2>
            <p className="text-xs text-navy-400">
              Ce que les agences cherchent et ne trouvent pas — vos prochains
              articles à écrire.
            </p>
          </div>
          <ul className="divide-y divide-navy-100">
            {misses.data.slice(0, 12).map((m) => (
              <li
                key={m.query}
                className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm"
              >
                <span className="truncate text-navy-700">« {m.query} »</span>
                <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-800 ring-1 ring-amber-100">
                  {m.count}×
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {categories.length === 0 ? (
        <Panel>
          <EmptyInline>Aucune catégorie. Créez-en une pour commencer.</EmptyInline>
        </Panel>
      ) : (
        <div className="space-y-4">
          {categories.map((c) => (
            <Panel key={c.id} padded={false}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-navy-100 px-5 py-4">
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold ${
                      TONE_SQUARE[c.tone] ?? TONE_SQUARE.slate
                    }`}
                  >
                    {c.title.charAt(0).toUpperCase()}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-bold text-navy-800">{c.title}</h2>
                      <span className="rounded-full bg-navy-50 px-2 py-0.5 font-mono text-[11px] text-navy-400 ring-1 ring-navy-100">
                        {c.key}
                      </span>
                    </div>
                    <p className="text-xs text-navy-400">
                      {c.articles.length} article
                      {c.articles.length > 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <IconBtn
                    label="Éditer la catégorie"
                    onClick={() =>
                      setCat({
                        id: c.id,
                        key: c.key,
                        title: c.title,
                        description: c.description,
                        icon: c.icon,
                        tone: c.tone,
                        ordre: c.ordre,
                      })
                    }
                  >
                    {ICON.pencil}
                  </IconBtn>
                  <IconBtn
                    label="Supprimer la catégorie"
                    danger
                    onClick={() =>
                      setDel({ type: "category", id: c.id, label: c.title })
                    }
                  >
                    {ICON.trash}
                  </IconBtn>
                </div>
              </div>
              {c.articles.length === 0 ? (
                <div className="px-5 py-4">
                  <EmptyInline>Aucun article dans cette catégorie.</EmptyInline>
                </div>
              ) : (
                <ul className="divide-y divide-navy-100">
                  {c.articles.map((a) => (
                    <li
                      key={a.id}
                      className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-navy-50/40"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-navy-800">
                          {a.question}
                        </p>
                        <p className="font-mono text-xs text-navy-400">
                          /{a.slug}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {a.helpfulYes + a.helpfulNo > 0 ? (
                          <span
                            className="mr-1 text-xs text-navy-400"
                            title="Retours utilisateurs (utile / pas utile)"
                          >
                            👍 {a.helpfulYes} · 👎 {a.helpfulNo}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => togglePublish.mutate(a)}
                          title={a.published ? "Dépublier" : "Publier"}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 transition-colors ${
                            a.published
                              ? "bg-emerald-50 text-emerald-700 ring-emerald-100 hover:bg-emerald-100"
                              : "bg-amber-50 text-amber-800 ring-amber-100 hover:bg-amber-100"
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              a.published ? "bg-emerald-500" : "bg-amber-500"
                            }`}
                          />
                          {a.published ? "Publié" : "Brouillon"}
                        </button>
                        <IconBtn
                          label="Éditer l'article"
                          onClick={() =>
                            setArticle({
                              id: a.id,
                              categoryId: a.categoryId,
                              slug: a.slug,
                              question: a.question,
                              body: a.body,
                              keywords: a.keywords ?? "",
                              published: a.published,
                              ordre: a.ordre,
                            })
                          }
                        >
                          {ICON.pencil}
                        </IconBtn>
                        <IconBtn
                          label="Supprimer l'article"
                          danger
                          onClick={() =>
                            setDel({
                              type: "article",
                              id: a.id,
                              label: a.question,
                            })
                          }
                        >
                          {ICON.trash}
                        </IconBtn>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          ))}
        </div>
      )}

      {/* Modale article */}
      <Modal
        open={!!article}
        onClose={() => setArticle(null)}
        title={article?.id ? "Éditer l'article" : "Nouvel article"}
      >
        {article ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              saveArticle.mutate(article);
            }}
          >
            <Select
              label="Catégorie"
              options={catOptions}
              value={article.categoryId}
              onChange={(e) =>
                setArticle({ ...article, categoryId: e.target.value })
              }
            />
            {!article.id ? (
              <Input
                label="Slug (ancre URL, non modifiable ensuite)"
                value={article.slug}
                onChange={(e) =>
                  setArticle({
                    ...article,
                    slug: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, ""),
                  })
                }
              />
            ) : null}
            <Input
              label="Question"
              value={article.question}
              onChange={(e) =>
                setArticle({ ...article, question: e.target.value })
              }
            />
            <Textarea
              label="Réponse — Markdown supporté (## titres, - listes, **gras**, [lien](url), ![img](url))"
              rows={8}
              value={article.body}
              onChange={(e) => setArticle({ ...article, body: e.target.value })}
            />
            <Input
              label="Mots-clés (recherche, séparés par espace)"
              value={article.keywords}
              onChange={(e) =>
                setArticle({ ...article, keywords: e.target.value })
              }
            />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-navy-700">
                <input
                  type="checkbox"
                  checked={article.published}
                  onChange={(e) =>
                    setArticle({ ...article, published: e.target.checked })
                  }
                />
                Publié
              </label>
              <Input
                label="Ordre"
                type="number"
                value={String(article.ordre)}
                onChange={(e) =>
                  setArticle({ ...article, ordre: Number(e.target.value) || 0 })
                }
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setArticle(null)}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={saveArticle.isPending}>
                {saveArticle.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </form>
        ) : null}
      </Modal>

      {/* Modale catégorie */}
      <Modal
        open={!!cat}
        onClose={() => setCat(null)}
        title={cat?.id ? "Éditer la catégorie" : "Nouvelle catégorie"}
      >
        {cat ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              saveCat.mutate(cat);
            }}
          >
            {!cat.id ? (
              <Input
                label="Clé (identifiant URL, non modifiable ensuite)"
                value={cat.key}
                onChange={(e) =>
                  setCat({
                    ...cat,
                    key: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, ""),
                  })
                }
              />
            ) : null}
            <Input
              label="Titre"
              value={cat.title}
              onChange={(e) => setCat({ ...cat, title: e.target.value })}
            />
            <Input
              label="Description"
              value={cat.description}
              onChange={(e) => setCat({ ...cat, description: e.target.value })}
            />
            <Input
              label="Icône"
              hint={ICON_HINT}
              value={cat.icon}
              onChange={(e) => setCat({ ...cat, icon: e.target.value })}
            />
            <Select
              label="Couleur (tone)"
              options={TONES}
              value={cat.tone}
              onChange={(e) => setCat({ ...cat, tone: e.target.value })}
            />
            <Input
              label="Ordre"
              type="number"
              value={String(cat.ordre)}
              onChange={(e) =>
                setCat({ ...cat, ordre: Number(e.target.value) || 0 })
              }
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setCat(null)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saveCat.isPending}>
                {saveCat.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </form>
        ) : null}
      </Modal>

      {/* Confirmation suppression */}
      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        title="Confirmer la suppression"
        confirmLabel="Supprimer"
        destructive
        busy={doDelete.isPending}
        description={
          del?.type === "category"
            ? `Supprimer la catégorie « ${del.label} » et tous ses articles ? Cette action est irréversible.`
            : `Supprimer l'article « ${del?.label ?? ""} » ? Cette action est irréversible.`
        }
        onConfirm={() => {
          if (del) doDelete.mutate(del);
        }}
      />
    </div>
  );
}
